import React, { useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    TextField,
    Skeleton,
} from "@mui/material";
import {
    CheckCircleOutline,
    PendingActionsOutlined,
    ReceiptLongOutlined,
    ErrorOutline,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";

import CustomTabs from "components/Shared/CustomTabs";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useTablePagination } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyGetVendorPaymentDetailsQuery } from "store/services/vendorProfileApi";
import TableFilter from "components/Shared/AppTable/TableFilter";

const TABS = [
    {
        label: "COD paid",
        value: "cod-paid",
        icon: <CheckCircleOutline fontSize="small" />,
        summaryLabel: "Total COD paid",
        summaryKey: "totalCODPaid",
        chipColor: "success",
    },
    {
        label: "COD not paid",
        value: "cod-unpaid",
        icon: <PendingActionsOutlined fontSize="small" />,
        summaryLabel: "Total COD not paid",
        summaryKey: "totalCODNotPaid",
        chipColor: "warning",
    },
    {
        label: "Charge paid",
        value: "charge-paid",
        icon: <ReceiptLongOutlined fontSize="small" />,
        summaryLabel: "Total charge paid",
        summaryKey: "totalChargePaid",
        chipColor: "info",
    },
    {
        label: "Charge not paid",
        value: "charge-unpaid",
        icon: <ErrorOutline fontSize="small" />,
        summaryLabel: "Total charge not paid",
        summaryKey: "totalChargeNotPaid",
        chipColor: "error",
    },
];

const STATUS_CHIP = {
    Paid: { label: "Paid", color: "success" },
    "Not Paid": { label: "Not paid", color: "warning" },
};

const formatCurrency = (val) =>
    `Rs. ${Number(val ?? 0).toLocaleString("en-IN")}`;

const VendorPaymentDetails = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { appendParams } = useCustomParams();

    const activeTab = searchParams.get("tab") || "cod-paid";
    const fromDate = searchParams.get("fromDate") || null;
    const toDate = searchParams.get("toDate") || null;

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [fetchDetails, { data, isLoading, isFetching }] =
        useLazyGetVendorPaymentDetailsQuery();

    // same pattern as VendorTicketList
    const handleTabChange = (newTab) => {
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set("tab", newTab);
        setSearchParams(currentParams);
    };

    useEffect(() => {
        fetchDetails({
            tab: activeTab,
            page: currentPage,
            limit: perPage,
            ...(fromDate && { fromDate }),
            ...(toDate && { toDate }),
        });
    }, [activeTab, currentPage, perPage, fromDate, toDate, fetchDetails]);

    const columns = [
        {
            name: "Shipment ID",
            selector: (row) => row?.shipmentId,
            cell: (row) => row?.shipmentId || "—",
        },
        {
            name: "Type",
            selector: (row) => row?.chargeType,
            cell: (row) => row?.chargeType || "—",
        },
        {
            name: "Amount",
            selector: (row) => row?.amount,
            cell: (row) => formatCurrency(row?.amount),
        },
        {
            name: "Status",
            cell: (row) => {
                const chip = STATUS_CHIP[row?.paymentStatus] ?? {
                    label: row?.paymentStatus,
                    color: "default",
                };
                return (
                    <Chip
                        label={chip.label}
                        color={chip.color}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        },
        {
            name: "Date",
            cell: (row) => <AppTableDateTime date={row?.date} />,
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box>
                {/* Summary cards — all 4 always visible, clicking switches tab */}
                <Grid container spacing={2} mb={2}>
                    {TABS.map((t) => (
                        <Grid key={t.value} item xs={6} sm={3}>
                            <Paper
                                onClick={() => handleTabChange(t.value)}
                                sx={{
                                    borderRadius: 3,
                                    p: 1.5,
                                    border: "0.5px solid",
                                    borderColor:
                                        activeTab === t.value ? `${t.chipColor}.main` : "divider",
                                    bgcolor:
                                        activeTab === t.value
                                            ? `${t.chipColor}.50`
                                            : "background.paper",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": { transform: "scale(1.03)" },
                                }}
                            >
                                {isLoading ? (
                                    <Skeleton height={50} />
                                ) : (
                                    <>
                                        <Typography variant="caption" color="text.secondary">
                                            {t.summaryLabel}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={500} mt={0.5}>
                                            {formatCurrency(data?.summary?.[t.summaryKey] ?? 0)}
                                        </Typography>
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Tabs — same as VendorTicketList pattern */}
                <CustomTabs
                    tabs={TABS.map((t) => ({ label: t.label, value: t.value }))}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                {/* Date filters — same as GeneralLedgerFilter pattern */}
                <Box display="flex" gap={1.5} py={1.5}>
                    <TableFilter
                        disabled={isLoading || isFetching}
                        initialFilters={2}
                        onClear={() => {
                            appendParams({ fromDate: "", toDate: "" });
                        }}
                    >
                        <DatePicker
                            label="From date"
                            value={fromDate ? moment(fromDate) : null}
                            onChange={(val) =>
                                appendParams({ fromDate: val ? val.format("YYYY-MM-DD") : "" })
                            }
                            maxDate={toDate ? moment(toDate) : moment()}
                            disableFuture
                            renderInput={(params) => (
                                <TextField {...params} size="small" fullWidth />
                            )}
                        />
                        <DatePicker
                            label="To date"
                            value={toDate ? moment(toDate) : null}
                            onChange={(val) =>
                                appendParams({ toDate: val ? val.format("YYYY-MM-DD") : "" })
                            }
                            minDate={fromDate ? moment(fromDate) : undefined}
                            maxDate={moment()}
                            disableFuture
                            renderInput={(params) => (
                                <TextField {...params} size="small" fullWidth />
                            )}
                        />
                    </TableFilter>
                </Box>

                {/* Table — same AppTable pattern as VendorTicketList */}
                <AppTable
                    columns={columns}
                    data={data?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={data?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default VendorPaymentDetails;